
<template>
    <div>
      <Header />
  
      <!-- ── Back + breadcrumb ── -->
      <div class="back-row">
        <button class="btn-back" @click="$router.back()">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
      <div class="page-header">
        <div>
          <h5 class="page-title">New Employee</h5>
          <p class="page-sub">
            <router-link :to="$url('/manage-employees')" class="breadcrumb-link">Employees</router-link>
            <span class="breadcrumb-sep">/</span>
            <span>New Employee</span>
          </p>
        </div>
      </div>
  
      <div class="form-wrapper">
  
        <!-- ══════════════════════════════
             STEP INDICATOR
        ══════════════════════════════ -->
        <div class="stepper-card">
          <div class="stepper">
            <template v-for="(step, i) in steps" :key="step.number">
              <div class="step">
                <div class="step-circle"
                  :class="{
                    'step-circle--done':    currentStep > step.number,
                    'step-circle--active':  currentStep === step.number,
                    'step-circle--pending': currentStep < step.number,
                  }">
                  <font-awesome-icon v-if="currentStep > step.number" icon="check" />
                  <span v-else>{{ step.number }}</span>
                </div>
                <span class="step-label"
                  :class="currentStep >= step.number ? 'step-label--active' : 'step-label--pending'">
                  {{ step.title }}
                </span>
              </div>
              <div v-if="i < steps.length - 1" class="step-line"
                :class="currentStep > step.number ? 'step-line--done' : 'step-line--pending'" />
            </template>
          </div>
          <div class="step-mobile-label">
            Step {{ currentStep }} of {{ steps.length }} &mdash; {{ steps[currentStep - 1].title }}
          </div>
        </div>
  
        <!-- ══════════════════════════════
             FORM CARD
        ══════════════════════════════ -->
        <div class="form-card">
  
          <!-- ── Step 1: Personal Information ── -->
          <div v-if="currentStep === 1">
            <h2 class="step-title">Personal Information</h2>
  
            <!-- Profile picture -->
            <div class="avatar-section">
              <div class="avatar-preview" v-if="profilePreview">
                <img :src="profilePreview" alt="Profile preview" class="avatar-img" />
                <button type="button" class="avatar-remove" @click="removePhoto">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
              <div class="avatar-placeholder" v-else>
                <font-awesome-icon icon="upload" class="avatar-placeholder-icon" />
              </div>
              <div>
                <label for="profilePicInput" class="btn-upload">
                  {{ profilePreview ? 'Change Photo' : 'Upload Photo' }}
                </label>
                <input id="profilePicInput" type="file" accept="image/*" style="display:none"
                  @change="onPhotoChange" />
                <p class="upload-hint">JPG, PNG or GIF. Max 2 MB.</p>
              </div>
            </div>
  
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">First Name <span class="req">*</span></label>
                <input type="text" class="field-input" v-model="form.first_name"
                  :class="{ 'field-input--error': errors.first_name }" placeholder="Enter first name" />
                <span v-if="errors.first_name" class="field-error">{{ errors.first_name }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Middle Name</label>
                <input type="text" class="field-input" v-model="form.middle_name" placeholder="Enter middle name" />
              </div>
              <div class="field-group">
                <label class="field-label">Last Name <span class="req">*</span></label>
                <input type="text" class="field-input" v-model="form.last_name"
                  :class="{ 'field-input--error': errors.last_name }" placeholder="Enter last name" />
                <span v-if="errors.last_name" class="field-error">{{ errors.last_name }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Official Email <span class="req">*</span></label>
                <input type="email" class="field-input" v-model="form.email"
                  :class="{ 'field-input--error': errors.email }" placeholder="you@example.com" />
                <span v-if="errors.email" class="field-error">{{ errors.email }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Personal Email </label>
                <input type="email" class="field-input" v-model="form.personal_email"
                  :class="{ 'field-input--error': errors.personal_email }" placeholder="you@example.com" />
                <span v-if="errors.personal_email" class="field-error">{{ errors.personal_email }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Phone <span class="req">*</span></label>
                <input type="tel" class="field-input" v-model="form.phone"
                  :class="{ 'field-input--error': errors.phone }" placeholder="+234 800 000 0000" />
                <span v-if="errors.phone" class="field-error">{{ errors.phone }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Date of Birth <span class="req">*</span></label>
                <input type="date" class="field-input" v-model="form.dob"
                  :class="{ 'field-input--error': errors.dob }" :max="today" />
                <span v-if="errors.dob" class="field-error">{{ errors.dob }}</span>
              </div>
              <div class="field-group">
                <label class="field-label">Gender <span class="req">*</span></label>
                <select class="field-input field-select" v-model="form.gender"
                  :class="{ 'field-input--error': errors.gender }">
                  <option value="">— Select gender —</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <span v-if="errors.gender" class="field-error">{{ errors.gender }}</span>
              </div>

              <div class="field-group">
                <label class="field-label">Highest Qualification</label>
                <select class="field-input field-select" v-model="form.highest_qualification">
                  <option value="">— Select qualification —</option>
                  <option value="no_formal_education">No Formal Education</option>
                  <option value="primary_school">Primary School</option>
                  <option value="secondary_school">Secondary School / GCSE / O-Level</option>
                  <option value="a_level">A-Levels / WAEC</option>
                  <option value="vocational_cert">Vocational / Technical Certificate</option>
                  <option value="ond">OND / Foundation Degree</option>
                  <option value="hnd">HND / Associate Degree</option>
                  <option value="bachelors">Bachelor's Degree (B.Sc / B.A / B.Ed)</option>
                  <option value="pgd">Postgraduate Diploma (PGD)</option>
                  <option value="masters">Master's Degree (M.Sc / MBA / M.A)</option>
                  <option value="doctorate">Doctorate (PhD / MD)</option>
                  <option value="professional_cert">Professional Certification</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div class="field-group">
                <label class="field-label">Post Code </label>
                <input type="text" class="field-input" v-model="form.post_code"
                    :class="{ 'field-input--error': errors.post_code }" placeholder="IG8 7B" />
                <span v-if="errors.post_code" class="field-error">{{ errors.post_code }}</span>
               </div>

              <div class="field-group field-group--full">
                <label class="field-label">Residential Address <span class="req">*</span></label>
                <textarea class="field-input" rows="3" v-model="form.address"
                  :class="{ 'field-input--error': errors.address }"
                  placeholder="Enter residential address"></textarea>
                <span v-if="errors.address" class="field-error">{{ errors.address }}</span>
              </div>
            </div>
          </div>
  
  
          <!-- ── Step 2: Employment Information ── -->
          <div v-if="currentStep === 2">
            <h2 class="step-title">Employment Information</h2>
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">Department <span class="req">*</span></label>
                <select class="field-input field-select" v-model="form.department_id"
                  :class="{ 'field-input--error': errors.department_id }"
                  @change="onDepartmentChange">
                  <option value="">— Select department —</option>
                  <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
                </select>
                <span v-if="errors.department_id" class="field-error">{{ errors.department_id }}</span>
              </div>
  
              <div class="field-group">
                <label class="field-label">Job Position</label>
                <select class="field-input field-select" v-model="form.position_id"
                  :disabled="!form.department_id || loadingPositions">
                  <option value="">
                    {{ loadingPositions ? 'Loading positions...' :
                       !form.department_id ? 'Select a department first' :
                       positions.length === 0 ? 'No positions available' :
                       '— Select position —' }}
                  </option>
                  <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>
  
              <div class="field-group">
                <label class="field-label">Unit</label>
                <select class="field-input field-select" v-model="form.unit_id"
                  :disabled="!form.department_id || loadingPositions">
                  <option value="">
                    {{ !form.department_id ? 'Select a department first' :
                       units.length === 0 ? 'No units available' :
                       '— Select unit —' }}
                  </option>
                  <option v-for="u in units" :key="u.id" :value="u.id">{{ u.name }}</option>
                </select>
              </div>
  
              <div class="field-group">
                <label class="field-label">Pay Grade</label>
                <select class="field-input field-select" v-model="form.paygrade_id">
                  <option value="">— Select pay grade —</option>
                  <option v-for="pg in paygrades" :key="pg.id" :value="pg.id">
                    {{ pg.name }} ({{ pg.min_salary }} – {{ pg.max_salary }})
                  </option>
                </select>
              </div>

              <div class="field-group field-group--full">
                <label class="field-label">Business Location(s)</label>
                <div class="loc-checklist" :class="{ 'field-input--error': errors.location_ids }">
                  <div v-if="locations.length === 0" class="loc-checklist-empty">No locations available</div>
                  <label v-for="loc in locations" :key="loc.id" class="loc-checklist-item">
                    <input type="checkbox" :value="loc.id" v-model="form.location_ids" />
                    <span>{{ loc.name }}</span>
                  </label>
                </div>
                <span v-if="errors.location_ids" class="field-error">{{ errors.location_ids }}</span>
              </div>

              <div v-if="form.location_ids.length > 0" class="field-group">
                <label class="field-label">Main Location <span class="req">*</span></label>
                <select class="field-input field-select" v-model="form.location_id" :class="{ 'field-input--error': errors.location_id }">
                  <option value="">— Select main location —</option>
                  <option v-for="id in form.location_ids" :key="id" :value="id">{{ locationName(id) }}</option>
                </select>
                <span v-if="errors.location_id" class="field-error">{{ errors.location_id }}</span>
              </div>

                <div class="field-group" v-click-outside="() => supervisorOpen = false">
                  <label class="field-label">Direct Line Manager/Supervisor</label>
                  <div class="ss-wrap">
                    <input
                      class="field-input ss-input"
                      :class="{ 'ss-input--open': supervisorOpen }"
                      :value="supervisorOpen ? supervisorSearch : supervisorLabel"
                      @focus="supervisorOpen = true; supervisorSearch = ''"
                      @input="supervisorSearch = $event.target.value; supervisorOpen = true"
                      placeholder="Search employees..."
                      autocomplete="off"
                    />
                    <span v-if="form.supervisor && !supervisorOpen" class="ss-clear" @mousedown.prevent="form.supervisor = ''; supervisorSearch = ''">×</span>
                    <div v-if="supervisorOpen" class="ss-drop">
                      <div v-if="!filteredSupervisors.length" class="ss-empty">No employees found</div>
                      <div
                        v-for="emp in filteredSupervisors" :key="emp.id"
                        class="ss-option"
                        :class="{ 'ss-option--active': form.supervisor === emp.id }"
                        @mousedown.prevent="form.supervisor = emp.id; supervisorOpen = false; supervisorSearch = ''"
                      >
                        {{ emp.first_name + ' ' + emp.last_name }}
                        <span v-if="emp.employee_no" class="ss-emp-no">{{ emp.employee_no }}</span>
                      </div>
                    </div>
                  </div>
                </div>

  
              <div class="field-group">
                <label class="field-label">Employment Date <span class="req">*</span></label>
                <input type="date" class="field-input" v-model="form.hire_date"
                  :class="{ 'field-input--error': errors.hire_date }" :max="today" />
                <span v-if="errors.hire_date" class="field-error">{{ errors.hire_date }}</span>
              </div>

              <div class="field-group">
                <label class="field-label">Employment Type <span class="req">*</span></label>
                <select class="field-input field-select" v-model="form.employment_type"
                  :class="{ 'field-input--error': errors.employment_type }">
                  <option value="">— Select —</option>
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="casual">Casual</option>
                  <option value="intern">Intern</option>
                </select>
                <span v-if="errors.employment_type" class="field-error">{{ errors.employment_type }}</span>
              </div>

              <div class="field-group">
                <label class="field-label">Salary Type <span class="req">*</span></label>
                <select class="field-input field-select" v-model="form.salary_type"
                  :class="{ 'field-input--error': errors.salary_type }">
                  <option value="">— Select —</option>
                  <option value="fixed">Fixed Salary</option>
                  <option value="hourly">Hourly</option>
                  <option value="variable">Variable / Commission</option>
                </select>
                <span v-if="errors.salary_type" class="field-error">{{ errors.salary_type }}</span>
              </div>

              <div v-if="form.salary_type === 'fixed'" class="field-group">
                <label class="field-label">Basic Salary <span class="req">*</span></label>
                <input type="text" oninput="this.value=this.value.replace(/[^0-9]/g,'')" class="field-input" v-model="form.basic_salary"
                  :class="{ 'field-input--error': errors.basic_salary }" :max="today" />
                <span v-if="errors.basic_salary" class="field-error">{{ errors.basic_salary }}</span>
              </div>

              <div class="field-group">
                <label class="field-label">Weekly Contracted Hours</label>
                <input type="number" min="0" max="168" step="0.5" class="field-input" v-model="form.contracted_hours"
                  :class="{ 'field-input--error': errors.contracted_hours }"
                  placeholder="e.g. 37.5" />
                <span v-if="errors.contracted_hours" class="field-error">{{ errors.contracted_hours }}</span>
              </div>

              <div v-if="['hourly', 'variable', 'calculated'].includes(form.salary_type)" class="field-group">
                <label class="field-label">Hourly Rate <span class="req">*</span></label>
                <input type="number" min="0" step="0.01" class="field-input" v-model="form.hourly_rate"
                  :class="{ 'field-input--error': errors.hourly_rate }"
                  placeholder="e.g. 12.50" />
                <span v-if="errors.hourly_rate" class="field-error">{{ errors.hourly_rate }}</span>
              </div>

            </div>
          </div>


          <!-- ── Step 3: Financial Information ── -->
          <div v-if="currentStep === 3">
            <h2 class="step-title">Financial Information</h2>
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">Bank Name <span class="req">*</span></label>
                <select class="field-input field-select" v-model="form.bank_id"
                  :class="{ 'field-input--error': errors.bank_id }">
                  <option value="">— Select bank —</option>
                  <option v-for="b in filteredBanks" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>
                <span v-if="errors.bank_id" class="field-error">{{ errors.bank_id }}</span>
              </div>

              <div class="field-group">
                <label class="field-label">Account Number <span class="req">*</span></label>
                <input type="text" class="field-input" v-model="form.account_number"
                  :class="{ 'field-input--error': errors.account_number }"
                  :placeholder="bankCountry === 'GB' ? '8-digit account number' : '10-digit account number'"
                  :maxlength="bankCountry === 'GB' ? 8 : 10"
                  inputmode="numeric"
                  @input="onAccountNumberInput" />
                <span v-if="errors.account_number" class="field-error">{{ errors.account_number }}</span>
              </div>

              <div class="field-group field-group--full">
                <label class="field-label">Account Name <span class="req">*</span></label>
                <input type="text" class="field-input" v-model="form.account_name"
                  :class="{ 'field-input--error': errors.account_name }"
                  placeholder="Enter account name" />
                <span v-if="errors.account_name" class="field-error">{{ errors.account_name }}</span>
              </div>

              <div v-if="bankCountry === 'GB'" class="field-group field-group--full">
                <label class="field-label">Sort Code <span class="req">*</span></label>
                <input type="text" class="field-input"
                  :class="{ 'field-input--error': errors.bank_sortcode }"
                  :value="form.bank_sortcode"
                  placeholder="e.g. 12-34-56"
                  maxlength="8" inputmode="numeric"
                  @input="onSortCodeInput" />
                <span v-if="errors.bank_sortcode" class="field-error">{{ errors.bank_sortcode }}</span>
              </div>

              <div class="field-group">
                <label class="field-label">Pension Provider</label>
                <select class="field-input field-select" v-model="form.pension_provider_id">
                  <option value="">— Select provider —</option>
                  <option v-for="p in filteredPensionProviders" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>

              <div class="field-group">
                <label class="field-label">Pension ID Number</label>
                <input type="text" class="field-input" v-model="form.pension_id_number"
                  placeholder="Enter pension ID" />
              </div>

              <div class="field-group field-group--full">
                <label class="field-label">
                  {{ bankCountry === 'GB' ? 'National Insurance Number (NI)' : 'Tax Identification Number' }}
                </label>
                <input type="text" class="field-input" v-model="form.tax_id_number"
                  :class="{ 'field-input--error': errors.tax_id_number }"
                  :placeholder="bankCountry === 'GB' ? 'e.g. AB123456C' : 'Enter tax identification number'"
                  :maxlength="bankCountry === 'GB' ? 9 : undefined"
                  @input="bankCountry === 'GB' ? onNiNumberInput($event) : null" />
                <span v-if="errors.tax_id_number" class="field-error">{{ errors.tax_id_number }}</span>
              </div>

              <!-- ── UK / HMRC fields ── -->
              <template v-if="businessCountry === 'GB'">
                <div class="field-group--section-title">HMRC Details</div>

                <div class="field-group">
                  <label class="field-label">
                    Tax Code
                    <span class="field-hint">e.g. 1257L, BR, D0, K200</span>
                  </label>
                  <input type="text" class="field-input" v-model="form.tax_code"
                    placeholder="1257L" maxlength="10"
                    style="text-transform:uppercase"
                    @input="form.tax_code = form.tax_code.toUpperCase()" />
                </div>

                <div class="field-group">
                  <label class="field-label">NI Category</label>
                  <select class="field-input field-select" v-model="form.ni_category">
                    <option value="A">A — Standard</option>
                    <option value="B">B — Married women / widows reduced rate</option>
                    <option value="C">C — Over State Pension Age (employee exempt)</option>
                    <option value="D">D — Deferment (full rates)</option>
                    <option value="H">H — Apprentice under 25 (employer exempt)</option>
                    <option value="J">J — Deferment (employee primary exempt)</option>
                    <option value="M">M — Under 21 (employer exempt)</option>
                    <option value="Z">Z — Under 21 deferment (both exempt)</option>
                    <option value="X">X — Exempt</option>
                  </select>
                </div>

                <div class="field-group">
                  <label class="field-label">
                    Average Weekly Earnings (£)
                    <span class="field-hint">Used for SSP / SMP / SPP entitlement</span>
                  </label>
                  <input type="number" class="field-input" v-model.number="form.average_weekly_earnings"
                    placeholder="0.00" min="0" step="0.01" />
                </div>
              </template>
            </div>
          </div>
  
  
          <!-- ── Step 4: Documents ── -->
          <div v-if="currentStep === 4">
            <h2 class="step-title">Documents</h2>
            <p class="step-sub">Upload supporting documents for this employee. All files are optional.</p>
  
            <!-- Add document row -->
            <div class="doc-add-row">
              <div class="field-group" style="flex:1;">
                <label class="field-label">Document Type</label>
                <select class="field-input field-select" v-model="newDoc.type">
                  <option value="">— Select type —</option>
                  <option value="national_id">National ID</option>
                  <option value="passport">Passport</option>
                  <option value="drivers_license">Driver's Licence</option>
                  <option value="birth_certificate">Birth Certificate</option>
                  <option value="degree_certificate">Degree Certificate</option>
                  <option value="professional_cert">Professional Certificate</option>
                  <option value="resume">CV / Resume</option>
                  <option value="employment_contract">Employment Contract</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div class="field-group" style="flex:1;">
                <label class="field-label">Label <span class="doc-label-hint">(optional description)</span></label>
                <input type="text" class="field-input" v-model="newDoc.label"
                  placeholder="e.g. NIN Card, B.Sc Certificate" />
              </div>
              <div class="field-group" style="flex:1.4;">
                <label class="field-label">File</label>
                <label class="doc-file-btn" :class="{ 'doc-file-btn--chosen': newDoc.file }">
                  <font-awesome-icon :icon="newDoc.file ? 'file-alt' : 'paperclip'" class="me-1" />
                  {{ newDoc.file ? newDoc.file.name : 'Choose file...' }}
                  <input type="file" style="display:none" @change="onDocFileChange" />
                </label>
              </div>
              <div class="field-group" style="justify-content:flex-end; padding-top:22px;">
                <button type="button" class="btn-add-doc"
                  :disabled="!newDoc.type || !newDoc.file"
                  @click="addDocument">
                  <font-awesome-icon icon="plus" class="me-1" /> Add
                </button>
              </div>
            </div>
  
            <span v-if="errors.documents" class="field-error" style="margin-bottom:10px;display:block;">
              {{ errors.documents }}
            </span>
  
            <!-- Document list -->
            <div v-if="documents.length" class="doc-list">
              <div class="doc-list-header">
                <span>Type</span>
                <span>Label</span>
                <span>File</span>
                <span></span>
              </div>
              <div v-for="(doc, i) in documents" :key="i" class="doc-list-row">
                <span class="doc-type-badge">{{ docTypeLabel(doc.type) }}</span>
                <span class="text-muted-cell">{{ doc.label || '—' }}</span>
                <span class="doc-filename">
                  <font-awesome-icon icon="file-alt" class="me-1" />
                  {{ doc.file.name }}
                  <span class="doc-size">({{ formatFileSize(doc.file.size) }})</span>
                </span>
                <button type="button" class="doc-remove-btn" title="Remove" @click="removeDocument(i)">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </div>
  
            <div v-else class="doc-empty">
              <font-awesome-icon icon="folder-open" class="doc-empty-icon" />
              <p>No documents added yet.</p>
              <p class="doc-empty-hint">Use the form above to attach documents.</p>
            </div>
          </div>
  
  
          <!-- ── Step 5: Emergency Contact ── -->
          <div v-if="currentStep === 5">
            <h2 class="step-title">Emergency Contact</h2>
            <p class="step-sub">All fields are optional. Used in case of an emergency.</p>
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">Full Name</label>
                <input type="text" class="field-input" v-model="form.emergency_name"
                  placeholder="e.g. Jane Doe" />
              </div>
              <div class="field-group">
                <label class="field-label">Relationship</label>
                <select class="field-input field-select" v-model="form.emergency_relationship">
                  <option value="">— Select relationship —</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Parent">Parent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Child">Child</option>
                  <option value="Friend">Friend</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="field-group">
                <label class="field-label">Phone Number</label>
                <input type="tel" class="field-input" v-model="form.emergency_phone"
                  placeholder="+234 800 000 0000" />
              </div>
              <div class="field-group">
                <label class="field-label">Email Address</label>
                <input type="email" class="field-input" v-model="form.emergency_email"
                  placeholder="contact@example.com" />
              </div>
              <div class="field-group field-group--full">
                <label class="field-label">Address</label>
                <textarea class="field-input" rows="3" v-model="form.emergency_address"
                  placeholder="Enter contact's residential address"></textarea>
              </div>
            </div>
          </div>
  
  
          <!-- ── Navigation ── -->
          <div class="form-nav">
            <button type="button" class="btn-outline"
              :disabled="currentStep === 1"
              @click="prevStep">
              <font-awesome-icon icon="arrow-left" class="me-1" /> Previous
            </button>
            <button type="button" class="btn-primary-action"
              :disabled="submitting"
              @click="nextOrSubmit">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else-if="currentStep === 5" icon="check" class="me-1" />
              {{ submitting ? 'Creating...' : currentStep === 5 ? 'Create Employee' : 'Next Step' }}
              <font-awesome-icon v-if="!submitting && currentStep < 5" icon="arrow-right" class="ms-1" />
            </button>
          </div>
  
        </div>
      </div>
    </div>
  </template>
  
  
  <script>
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  
  export default {
    name: 'NewEmployeePage',

    components: { Header },

    directives: {
      clickOutside: {
        mounted(el, binding) {
          el._coHandler = e => { if (!el.contains(e.target)) binding.value(e); };
          document.addEventListener('mousedown', el._coHandler);
        },
        unmounted(el) { document.removeEventListener('mousedown', el._coHandler); },
      },
    },
  
    data() {
      return {
        currentStep: 1,
        submitting:  false,
  
        steps: [
          { number: 1, title: 'Personal Info' },
          { number: 2, title: 'Employment' },
          { number: 3, title: 'Financial' },
          { number: 4, title: 'Documents' },
          { number: 5, title: 'Emergency Contact' },
        ],
  
        // Profile photo
        profilePicture: null,
        profilePreview: null,
  
        // All form fields flat — all submitted in one FormData call
        form: {
          // Step 1
            first_name:   '',
            middle_name:  '',
            last_name:    '',
            email:        '',
            personal_email: '',
            phone:        '',
            dob:          '',
            gender:       '',
            highest_qualification: '',
            address:      '',
            post_code:       '',
            // Step 2
            department_id: '',
            position_id:   '',
            paygrade_id:   '',
            unit_id: '',
            location_ids: [],
            location_id: '',
            hire_date:       '',
            employment_type:  '',
            salary_type:      'fixed',
            basic_salary:     0,
            contracted_hours: '',
            hourly_rate:      '',
            supervisor: '',

            // Step 3
            bank_id:             '',
            account_number:      '',
            account_name:        '',
            bank_sortcode: '',
            pension_provider_id: '',
            pension_id_number:   '',
            tax_id_number:       '',
            // HMRC (GB only)
            tax_code:                  '1257L',
            ni_category:               'A',
            average_weekly_earnings:   0,
            
            // Step 5 — Emergency Contact
            emergency_name:         '',
            emergency_relationship: '',
            emergency_phone:        '',
            emergency_email:        '',
            emergency_address:      '',
        },
  
        // Supervisor search-select state
        supervisorSearch: '',
        supervisorOpen:   false,

        // Per-field validation errors
        errors: {},


        // ── Step 4: Documents ──────────────────────────────────────────
        // New document being composed
        newDoc: { type: '', label: '', file: null },
        // Confirmed document list
        documents: [],
  
        // Lookup data
        departments:      [],
        units:      [],
        positions:        [],
        paygrades:        [],
        banks:            [],
        pensionProviders: [],
        locations: [],
        employees: [],
        loadingPositions: false,
        businessCountry: '',

        toast: useToast(),
      };
    },

    computed: {
      today() {
        return new Date().toISOString().split('T')[0];
      },

      filteredSupervisors() {
        const q = this.supervisorSearch.trim().toLowerCase();
        if (!q) return this.employees;
        return this.employees.filter(e =>
          `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
          (e.employee_no || '').toLowerCase().includes(q)
        );
      },

      supervisorLabel() {
        if (!this.form.supervisor) return '';
        const emp = this.employees.find(e => e.id === this.form.supervisor);
        return emp ? `${emp.first_name} ${emp.last_name}` : '';
      },

      filteredBanks() {
        if (!this.businessCountry) return this.banks;
        return this.banks.filter(b => b.country_code?.toUpperCase() === this.businessCountry);
      },

      filteredPensionProviders() {
        if (!this.businessCountry) return this.pensionProviders;
        return this.pensionProviders.filter(p => p.country_code?.toUpperCase() === this.businessCountry);
      },

      selectedBank() {
        return this.filteredBanks.find(b => b.id == this.form.bank_id) ?? null;
      },

      bankCountry() {
        return this.selectedBank?.country_code?.toUpperCase() ?? this.businessCountry;
      },
    },
  
    watch: {
      bankCountry(newVal) {
        this.form.account_number = '';
        this.form.tax_id_number  = '';
        if (newVal !== 'GB') this.form.bank_sortcode = '';
        this.errors = { ...this.errors, account_number: undefined, bank_sortcode: undefined, tax_id_number: undefined };
      },
    },

    created() {
      this.fetchLookups();
      this.fetchBusinessCountry();
    },
  
    methods: {
      locationName(id) {
        const loc = this.locations.find(l => l.id === id);
        return loc ? loc.name : id;
      },

      removeLocation(id) {
        this.form.location_ids = this.form.location_ids.filter(l => l !== id);
        if (this.form.location_id === id) this.form.location_id = '';
      },


      fetchBusinessCountry() {
        axios.get('/admin/business/active')
          .then(({ data }) => {
            this.businessCountry = (data?.data?.country_code ?? '').toUpperCase();
          })
          .catch(() => {});
      },

      // ── Lookups ────────────────────────────────────────────────────────
      fetchLookups() {
        Promise.allSettled([
            axios.get('/payroll/department/active', { params: { status: [1], length: 200 } }),
            axios.get('/payroll/paygrade/active'),
            axios.get('/settings/allbank/active'),
            axios.get('/settings/pension-provider/active'),
            axios.get('/admin/location/active'),
            axios.get('/payroll/employees/active'),
        ]).then(([depts, grades, banks, pensions, locations, employees]) => {
            if (depts.status === 'fulfilled') {
            const d = depts.value.data;
            this.departments = Array.isArray(d.data?.data) ? d.data.data
                : Array.isArray(d.data) ? d.data : [];
            }

            if (grades.status === 'fulfilled') {
            const b = grades.value.data;
            this.paygrades = Array.isArray(b.data) ? b.data : b.data?.data ?? [];
            }

            if (banks.status === 'fulfilled') {
            const b = banks.value.data;
            this.banks = Array.isArray(b.data) ? b.data : b.data?.data ?? [];
            }
            if (pensions.status === 'fulfilled') {
            const p = pensions.value.data;
            this.pensionProviders = Array.isArray(p.data) ? p.data : p.data?.data ?? [];
            }

            if (locations.status === 'fulfilled') {
            const p = locations.value.data;
            this.locations = Array.isArray(p.data) ? p.data : p.data?.data ?? [];
            }

            if (employees.status === 'fulfilled') {
            const p = employees.value.data;
            this.employees = Array.isArray(p.data) ? p.data : p.data?.data ?? [];
            }
        });
       },
  
      onDepartmentChange() {
        this.form.position_id = '';
        this.form.unit_id     = '';
        this.positions = [];
        this.units     = [];
        if (!this.form.department_id) return;
        this.loadingPositions = true;
  
        axios.get('/payroll/position/active', { params: { department_id: this.form.department_id } })
          .then(({ data }) => {
            this.positions = Array.isArray(data.data) ? data.data : data.data?.data ?? [];
          })
          .catch(() => { /* intentionally silent */ })
          .finally(() => { this.loadingPositions = false; });
  
        axios.get('/payroll/unit/active', { params: { department_id: this.form.department_id } })
          .then(({ data }) => {
            this.units = Array.isArray(data.data) ? data.data : data.data?.data ?? [];
          })
          .catch(() => { /* intentionally silent */ });
      },
  
      onAccountNumberInput(e) {
        this.form.account_number = e.target.value.replace(/\D/g, '').slice(0, this.bankCountry === 'GB' ? 8 : 10);
        e.target.value = this.form.account_number;
      },

      formatSortCode(raw) {
        const digits = (raw || '').replace(/\D/g, '').slice(0, 6);
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0,2)}-${digits.slice(2)}`;
        return `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4)}`;
      },

      onSortCodeInput(e) {
        const formatted = this.formatSortCode(e.target.value);
        this.form.bank_sortcode = formatted;
        e.target.value = formatted;
      },

      onNiNumberInput(e) {
        this.form.tax_id_number = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 9);
        e.target.value = this.form.tax_id_number;
      },

      // ── Profile photo ──────────────────────────────────────────────────
      onPhotoChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        this.profilePicture = file;
        const reader = new FileReader();
        reader.onloadend = () => { this.profilePreview = reader.result; };
        reader.readAsDataURL(file);
      },
  
      removePhoto() {
        this.profilePicture = null;
        this.profilePreview = null;
      },
  
      // ── Documents ──────────────────────────────────────────────────────
      onDocFileChange(e) {
        this.newDoc.file = e.target.files?.[0] ?? null;
      },
  
      addDocument() {
        if (!this.newDoc.type || !this.newDoc.file) return;
        this.documents.push({ ...this.newDoc });
        this.newDoc = { type: '', label: '', file: null };
        // Reset the hidden file input
        const input = this.$el.querySelector('input[type="file"][style="display:none"]:last-of-type');
        if (input) input.value = '';
      },
  
      removeDocument(index) {
        this.documents.splice(index, 1);
      },
  
      docTypeLabel(type) {
        const map = {
          national_id:         'National ID',
          passport:            'Passport',
          drivers_license:     "Driver's Licence",
          birth_certificate:   'Birth Certificate',
          degree_certificate:  'Degree Certificate',
          professional_cert:   'Professional Cert',
          resume:              'CV / Resume',
          employment_contract: 'Employment Contract',
          other:               'Other',
        };
        return map[type] ?? type;
      },
  
      formatFileSize(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      },
  
      // ── Step validation ────────────────────────────────────────────────
      validateStep() {
        const errs = {};
  
        if (this.currentStep === 1) {
          if (!this.form.first_name.trim() || this.form.first_name.length < 2)
            errs.first_name = 'First name must be at least 2 characters.';
          if (!this.form.last_name.trim() || this.form.last_name.length < 2)
            errs.last_name = 'Last name must be at least 2 characters.';
          if (!this.form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email))
            errs.email = 'A valid email address is required.';
          if (!this.form.phone.trim() || this.form.phone.length < 10)
            errs.phone = 'A valid phone number is required.';
          if (!this.form.dob)
            errs.dob = 'Date of birth is required.';
          if (!this.form.gender)
            errs.gender = 'Gender is required.';
          if (!this.form.address.trim() || this.form.address.length < 5)
            errs.address = 'Residential address is required.';
        }
  
        if (this.currentStep === 2) {
          if (!this.form.department_id)
            errs.department_id = 'Department is required.';
          if (!this.form.hire_date)
            errs.hire_date = 'Employment date is required.';
          if (!this.form.employment_type)
            errs.employment_type = 'Employment type is required.';
          if (!this.form.salary_type)
            errs.salary_type = 'Salary type is required.';
          if (this.form.salary_type === 'fixed' && !this.form.basic_salary)
            errs.basic_salary = 'Basic salary is required for fixed salary type.';
          if (['hourly', 'variable', 'calculated'].includes(this.form.salary_type) && !this.form.hourly_rate)
            errs.hourly_rate = 'Hourly rate is required for this salary type.';
          if (this.form.location_ids.length > 0 && !this.form.location_id)
            errs.location_id = 'Please select a main location.';
        }
  
        if (this.currentStep === 3) {
          if (!this.form.bank_id)
            errs.bank_id = 'Bank is required.';

          const acct = String(this.form.account_number ?? '').replace(/\D/g, '');
          if (this.bankCountry === 'GB') {
            if (acct.length !== 8)
              errs.account_number = 'Account number must be exactly 8 digits for a UK bank.';
          } else if (this.bankCountry === 'NG') {
            if (acct.length !== 10)
              errs.account_number = 'Account number must be exactly 10 digits for a Nigerian bank.';
          } else {
            if (!acct.length)
              errs.account_number = 'Account number is required.';
          }

          if (!this.form.account_name.trim() || this.form.account_name.length < 2)
            errs.account_name = 'Account name is required.';

          if (this.bankCountry === 'GB') {
            const sc = String(this.form.bank_sortcode ?? '').replace(/\D/g, '');
            if (sc.length !== 6)
              errs.bank_sortcode = 'Sort code must be exactly 6 digits.';

            const ni = String(this.form.tax_id_number ?? '').trim().toUpperCase();
            if (ni && !/^[A-Z]{2}\d{6}[A-Z]$/.test(ni))
              errs.tax_id_number = 'National Insurance Number must be 2 letters, 6 digits, then 1 letter (e.g. AB123456C).';
          }
        }
  
        // Step 4 (documents) has no required fields
  
        this.errors = errs;
        return Object.keys(errs).length === 0;
      },
  
      // ── Navigation ─────────────────────────────────────────────────────
      prevStep() {
        if (this.currentStep > 1) {
          this.errors = {};
          this.currentStep--;
        }
      },
  
      nextOrSubmit() {
        if (!this.validateStep()) return;

        if (this.currentStep < 5) {
          this.errors = {};
          this.currentStep++;
          if (this.currentStep === 3 && !this.form.account_name.trim()) {
            this.form.account_name = [this.form.first_name, this.form.middle_name, this.form.last_name]
              .filter(Boolean).join(' ');
          }
        } else {
          this.submitEmployee();
        }
      },
  
      // ── Submit — single FormData call with everything ──────────────────
      async submitEmployee() {
        this.submitting = true;
        try {
          const fd = new FormData();
  
          // ── Personal ──
          fd.append('first_name',  this.form.first_name);
            fd.append('middle_name', this.form.middle_name);
            fd.append('last_name',   this.form.last_name);
            fd.append('email',       this.form.email);
            fd.append('personal_email', this.form.personal_email);
            fd.append('phone_number',       this.form.phone);
            fd.append('dob',         this.form.dob);
            fd.append('address',     this.form.address);
            fd.append('post_code',     this.form.post_code);
            fd.append('gender',                this.form.gender);
            fd.append('highest_qualification', this.form.highest_qualification || '');
            if (this.profilePicture) fd.append('avatar', this.profilePicture);
  
          // ── Employment ──
          fd.append('department_id', this.form.department_id || '');
            fd.append('unit_id', this.form.unit_id || '');
            fd.append('position_id',   this.form.position_id   || '');
            fd.append('pay_grade_id',   this.form.paygrade_id   || '');
            fd.append('location_id',   this.form.location_id   || '');
            this.form.location_ids.forEach(id => fd.append('location_ids[]', id));
            fd.append('hire_date',       this.form.hire_date);
            fd.append('employment_type',  this.form.employment_type  || '');
            fd.append('salary_type',      this.form.salary_type      || '');
            fd.append('basic_salary',     this.form.basic_salary     || '');
            fd.append('contracted_hours', this.form.contracted_hours || '');
            fd.append('hourly_rate',      this.form.hourly_rate      || '');
            fd.append('supervisor',     this.form.supervisor);
            if (this.form.paygrade_id) fd.append('paygrade_id', this.form.paygrade_id);
  
          // ── Financial ──
          fd.append('all_bank_id',             String(this.form.bank_id));
            fd.append('bank_account_no',      this.form.account_number);
            fd.append('bank_account_name',        this.form.account_name);
            fd.append('bank_sortcode',        this.form.bank_sortcode);
            fd.append('pension_company_id', String(this.form.pension_provider_id || ''));
            fd.append('pension_number',   this.form.pension_id_number || '');
            fd.append('tax_number',       this.form.tax_id_number     || '');

          // ── HMRC (GB only) ──
          if (this.businessCountry === 'GB') {
            fd.append('tax_code',                this.form.tax_code || '1257L');
            fd.append('ni_category',             this.form.ni_category || 'A');
            fd.append('average_weekly_earnings', this.form.average_weekly_earnings || 0);
          }

          // ── Emergency Contact ──
          fd.append('emergency_name',         this.form.emergency_name         || '');
          fd.append('emergency_relationship', this.form.emergency_relationship || '');
          fd.append('emergency_phone',        this.form.emergency_phone        || '');
          fd.append('emergency_email',        this.form.emergency_email        || '');
          fd.append('emergency_address',      this.form.emergency_address      || '');
  
          // ── Documents ──
          // Backend receives: documents[0][type], documents[0][label], documents[0][file]
          // (Laravel array syntax in FormData)
          this.documents.forEach((doc, i) => {
            fd.append(`documents[${i}][type]`,  doc.type);
            fd.append(`documents[${i}][label]`, doc.label || '');
            fd.append(`documents[${i}][file]`,  doc.file, doc.file.name);
          });
  
          const { data } = await axios.post('/payroll/employees', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
  
          this.toast.success(data?.message || 'Employee created successfully.');
          this.$router.push('/manage-employees');
  
        } catch (err) {
          const msg = this.flatError(err.response?.data)
            || err.message
            || 'Failed to create employee. Please try again.';
          this.toast.error(msg);
        } finally {
          this.submitting = false;
        }
      },
  
      // ── Helpers ────────────────────────────────────────────────────────
  
      flatError(data) {
        if (!data || typeof data !== 'object') return null;
        const msg = data.message;
        if (typeof msg === 'string') return msg;
        if (Array.isArray(msg)) return msg[0];
        if (typeof msg === 'object') return Object.values(msg).flat()[0] ?? null;
        const errs = data.data;
        if (errs && typeof errs === 'object') return Object.values(errs).flat()[0] ?? null;
        return null;
      },
    },
  };
  </script>
  
  
  <style scoped>
  /* ── Page ── */
  .back-row    { margin-bottom:12px; }
  .btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; padding:0; transition:color .15s; }
  .btn-back:hover { color:#004AAD; }
  .page-header { margin-bottom:24px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
  .breadcrumb-link { color:#7a90b0; text-decoration:none; }
  .breadcrumb-link:hover { color:#004AAD; }
  .breadcrumb-sep { color:#c5d0e0; }
  .me-1 { margin-right:4px; }
  .me-2 { margin-right:8px; }
  .ms-1 { margin-left:4px; }
  .text-muted-cell { color:#4a5e78; font-size:13px; }

  /* ── Layout ── */
  .form-wrapper { max-width:900px; margin:0 auto; display:flex; flex-direction:column; gap:20px; padding-bottom:32px; box-sizing:border-box; }

  /* ── Stepper ── */
  .stepper-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px 28px; }
  .stepper      { display:flex; align-items:center; justify-content:center; }
  .step         { display:flex; flex-direction:column; align-items:center; gap:8px; min-width:0; }
  .step-circle  { width:44px; height:44px; border-radius:50%; border:2px solid; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:700; transition:all .2s; flex-shrink:0; }
  .step-circle--done    { background:#004AAD; border-color:#004AAD; color:#fff; }
  .step-circle--active  { background:#fff; border-color:#004AAD; color:#004AAD; }
  .step-circle--pending { background:#fff; border-color:#dde5f0; color:#94a3b8; }
  .step-label           { font-size:12px; font-weight:600; white-space:nowrap; }
  .step-label--active   { color:#1a2b45; }
  .step-label--pending  { color:#94a3b8; }
  .step-line            { width:48px; height:2px; margin:0 6px; margin-bottom:22px; flex-shrink:0; transition:background .2s; }
  .step-line--done      { background:#004AAD; }
  .step-line--pending   { background:#e4eaf2; }
  .step-mobile-label    { display:none; }

  /* ── Form card ── */
  .form-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:36px 40px; }
  .step-title { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 6px; }
  .step-sub   { font-size:13.5px; color:#7a90b0; margin:0 0 24px; }

  /* ── Profile photo ── */
  .avatar-section     { display:flex; align-items:center; gap:20px; margin-bottom:28px; flex-wrap:wrap; }
  .avatar-preview     { position:relative; flex-shrink:0; }
  .avatar-img         { width:80px; height:80px; border-radius:50%; object-fit:cover; border:3px solid #e4eaf2; }
  .avatar-remove      { position:absolute; top:-6px; right:-6px; width:26px; height:26px; border-radius:50%; background:#e03535; color:#fff; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:11px; transition:background .15s; }
  .avatar-remove:hover { background:#c02828; }
  .avatar-placeholder { width:80px; height:80px; border-radius:50%; border:2px dashed #dde5f0; background:#f8fafc; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .avatar-placeholder-icon { font-size:24px; color:#c0c9d8; }
  .btn-upload  { display:inline-block; padding:8px 16px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; font-weight:600; color:#1a2b45; background:#fff; cursor:pointer; transition:background .15s; }
  .btn-upload:hover { background:#f4f7fb; }
  .upload-hint { font-size:11.5px; color:#7a90b0; margin:6px 0 0; }

  /* ── Fields ── */
  .fields-grid       { display:grid; grid-template-columns:1fr 1fr; gap:18px; }
  .field-group       { display:flex; flex-direction:column; gap:5px; }
  .field-group--full { grid-column:1/-1; }
  .field-label       { font-size:13px; font-weight:600; color:#1a2b45; }
  .field-hint        { font-size:11px; font-weight:400; color:#64748b; margin-left:6px; }
  .field-group--section-title { grid-column:1/-1; font-size:12px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.05em; padding:10px 0 4px; border-bottom:1px solid #e2e8f0; margin-bottom:4px; }
  .req               { color:#e03535; }
  .field-input       { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:9px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s, box-shadow .15s; font-family:inherit; box-sizing:border-box; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input--error { border-color:#e03535; }
  .field-input:disabled { background:#f4f7fb; color:#94a3b8; cursor:not-allowed; }
  .field-select      { appearance:auto; cursor:pointer; }
  textarea.field-input { resize:vertical; }
  .field-error       { font-size:12px; color:#e03535; }

  /* ── Location checklist ── */
  .loc-checklist        { border:1.5px solid #dde5f0; border-radius:9px; max-height:160px; overflow-y:auto; padding:4px 0; }
  .loc-checklist-item   { display:flex; align-items:center; gap:10px; padding:8px 14px; cursor:pointer; font-size:13.5px; color:#1a2b45; transition:background .12s; }
  .loc-checklist-item:hover { background:#f4f7fb; }
  .loc-checklist-item input[type="checkbox"] { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
  .loc-checklist-empty  { padding:10px 14px; font-size:13px; color:#94a3b8; }

  /* ── Documents ── */
  .doc-add-row  { display:grid; grid-template-columns:1fr 1fr 1.4fr auto; gap:14px; align-items:flex-end; padding:20px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; margin-bottom:20px; }
  .doc-label-hint { font-size:11px; color:#94a3b8; font-weight:400; margin-left:4px; }
  .doc-file-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 12px; border:1.5px dashed #dde5f0; border-radius:9px; font-size:13px; color:#7a90b0; background:#fff; cursor:pointer; transition:all .15s; width:100%; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; box-sizing:border-box; }
  .doc-file-btn:hover { border-color:#004AAD; color:#004AAD; background:rgba(0,74,173,.03); }
  .doc-file-btn--chosen { border-style:solid; border-color:#004AAD; color:#004AAD; background:rgba(0,74,173,.04); }
  .btn-add-doc { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:9px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; white-space:nowrap; }
  .btn-add-doc:hover:not(:disabled) { background:#003b8e; }
  .btn-add-doc:disabled { opacity:.45; cursor:not-allowed; }
  .doc-list        { border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
  .doc-list-header { display:grid; grid-template-columns:160px 1fr 1fr 40px; gap:12px; padding:10px 16px; background:#f8fafc; border-bottom:1px solid #e4eaf2; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; }
  .doc-list-row    { display:grid; grid-template-columns:160px 1fr 1fr 40px; gap:12px; padding:12px 16px; align-items:center; border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .doc-list-row:last-child { border-bottom:none; }
  .doc-list-row:hover { background:#f8fafc; }
  .doc-type-badge  { display:inline-block; background:rgba(0,74,173,.08); color:#004AAD; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
  .doc-filename    { font-size:13px; color:#1a2b45; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .doc-size        { font-size:11.5px; color:#94a3b8; font-weight:400; }
  .doc-remove-btn  { width:28px; height:28px; border-radius:7px; border:1px solid #fecaca; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; transition:all .15s; flex-shrink:0; }
  .doc-remove-btn:hover { background:#e03535; color:#fff; }
  .doc-empty      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 24px; text-align:center; border:1px dashed #e4eaf2; border-radius:10px; }
  .doc-empty-icon { font-size:32px; color:#c0c9d8; margin-bottom:12px; }
  .doc-empty p    { font-size:14px; font-weight:600; color:#7a90b0; margin:0 0 4px; }
  .doc-empty-hint { font-size:12.5px; color:#94a3b8; }

  /* ── Nav buttons ── */
  .form-nav    { display:flex; justify-content:space-between; align-items:center; padding-top:28px; margin-top:28px; border-top:1px solid #e4eaf2; gap:12px; }
  .btn-primary-action { display:inline-flex; align-items:center; justify-content:center; gap:7px; background:#004AAD; color:#fff; font-size:13.5px; font-weight:600; padding:11px 28px; border-radius:9px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; justify-content:center; gap:7px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13.5px; font-weight:600; padding:11px 24px; border-radius:9px; cursor:pointer; transition:background .15s; font-family:inherit; }
  .btn-outline:hover:not(:disabled) { background:#f4f7fb; }
  .btn-outline:disabled { opacity:.45; cursor:not-allowed; }

  /* ── Tablet (≤768px) ── */
  @media (max-width:768px) {
    .form-card    { padding:28px 24px; }
    .stepper-card { padding:20px 16px; }
    .step-line    { width:32px; }
    .fields-grid  { grid-template-columns:1fr 1fr; gap:14px; }
    .doc-add-row  { grid-template-columns:1fr 1fr; }
  }

  /* ── Mobile (≤600px) ── */
  @media (max-width:600px) {
    .page-title      { font-size:17px; }
    .form-wrapper    { padding-bottom:24px; gap:14px; }
    .stepper-card    { padding:16px 12px; }

    .step-label      { display:none; }
    .step-circle     { width:34px; height:34px; font-size:13px; }
    .step-line       { width:20px; margin:0 4px; margin-bottom:0; }
    .step-mobile-label {
      display:block;
      text-align:center;
      margin-top:14px;
      font-size:13px;
      font-weight:600;
      color:#1a2b45;
    }

    .form-card       { padding:20px 16px; border-radius:12px; }
    .step-title      { font-size:18px; }

    .fields-grid     { grid-template-columns:1fr; gap:14px; }
    .field-group--full { grid-column:1; }
    .field-group--section-title { grid-column:1; }

    .avatar-section  { flex-direction:column; align-items:center; text-align:center; gap:14px; }

    .doc-add-row     { grid-template-columns:1fr; gap:12px; padding:16px; }
    .btn-add-doc     { width:100%; justify-content:center; }

    .doc-list-header { display:none; }
    .doc-list-row    { grid-template-columns:1fr auto; grid-template-rows:auto auto; gap:6px 8px; padding:12px 14px; }
    .doc-list-row .doc-type-badge { grid-column:1; }
    .doc-list-row .doc-filename   { grid-column:1; font-size:12px; }
    .doc-list-row .text-muted-cell { grid-column:1; }
    .doc-list-row .doc-remove-btn { grid-column:2; grid-row:1 / span 2; align-self:center; }

    .form-nav        { flex-direction:column-reverse; gap:10px; }
    .btn-primary-action,
    .btn-outline     { width:100%; font-size:14px; padding:13px; }
  }

  /* ── Small phones (≤400px) ── */
  @media (max-width:400px) {
    .form-card       { padding:16px 12px; }
    .step-circle     { width:30px; height:30px; font-size:12px; }
    .step-line       { width:12px; margin:0 2px; }
    .field-input     { font-size:13px; padding:8px 10px; }
    .btn-primary-action,
    .btn-outline     { font-size:13px; padding:12px; }
  }

  /* ── Search-select ── */
  .ss-wrap        { position:relative; }
  .ss-input       { width:100%; padding-right:28px; }
  .ss-input--open { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); border-bottom-left-radius:0; border-bottom-right-radius:0; }
  .ss-clear       { position:absolute; right:10px; top:50%; transform:translateY(-50%); font-size:16px; color:#aab8cc; cursor:pointer; line-height:1; user-select:none; }
  .ss-clear:hover { color:#e03535; }
  .ss-drop        { position:absolute; top:100%; left:0; right:0; background:#fff; border:1.5px solid #004AAD; border-top:none; border-bottom-left-radius:8px; border-bottom-right-radius:8px; max-height:220px; overflow-y:auto; z-index:300; box-shadow:0 8px 24px rgba(0,0,0,.1); }
  .ss-option      { padding:9px 13px; font-size:13px; color:#1a2b45; cursor:pointer; display:flex; align-items:center; justify-content:space-between; gap:8px; }
  .ss-option:hover, .ss-option--active { background:#eef4ff; }
  .ss-emp-no      { font-size:11px; color:#7a90b0; }
  .ss-empty       { padding:10px 13px; font-size:13px; color:#7a90b0; }
  </style>